import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { LIVE_CHECK } from './mockData';
import type { CdtCode, PayerOption } from './types';
import { formatDate } from './utils';
import { AllChecks } from './AllChecks';
import './livecheck.css';
import { isPlainClick } from '@/components/plainClick';

const fx = LIVE_CHECK;

/* ── Payer combobox — async-searchable, resolves to a payer id ─────────── */
function PayerCombobox({
  value,
  onChange,
  inputRef,
  invalid,
}: {
  value: PayerOption | null;
  onChange: (p: PayerOption | null) => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
  invalid: boolean;
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return [];
    return fx.payers
      .filter((p) => p.name.toLowerCase().includes(q) || p.id.toLowerCase().includes(q))
      .sort((a, b) => b.volume - a.volume || a.name.localeCompare(b.name))
      .slice(0, 8);
  }, [query]);

  if (value) {
    return (
      <span className="lc-combo-picked">
        {value.name} <span className="id">· {value.id}</span>
        <button type="button" className="lc-x" aria-label="Clear the payer" onClick={() => { onChange(null); setQuery(''); }}>×</button>
      </span>
    );
  }

  return (
    <div className="lc-combo">
      <input
        ref={inputRef}
        role="combobox"
        aria-expanded={open && results.length > 0}
        aria-controls="lc-payer-list"
        aria-autocomplete="list"
        aria-invalid={invalid}
        className="lc-combo-input"
        placeholder="Search by name or payer ID"
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); setActive(0); }}
        onFocus={() => { setOpen(true); }}
        onKeyDown={(e) => {
          if (!results.length) return;
          if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
          else if (e.key === 'Enter') { e.preventDefault(); const p = results[active]; if (p) { onChange(p); setOpen(false); } }
          else if (e.key === 'Escape') { setOpen(false); }
        }}
      />
      {open && query.trim().length >= 2 ? (
        <ul className="lc-combo-list" id="lc-payer-list" role="listbox">
          {results.length === 0 ? (
            <li className="lc-combo-empty">No payer matches. Can&rsquo;t find the payer? See Settings › Payers.</li>
          ) : (
            results.map((p, i) => (
              <li
                key={p.id}
                role="option"
                aria-selected={i === active}
                className="lc-combo-opt"
                onMouseEnter={() => { setActive(i); }}
                onMouseDown={(e) => { e.preventDefault(); onChange(p); setOpen(false); }}
              >
                <span className="nm">{p.name}</span>
                <span className="id">{p.id}</span>
                <span className="vol">{p.volume.toLocaleString('en-US')} claims</span>
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
}

/* ── CDT chip input ────────────────────────────────────────────────────── */
function CodeChipInput({ codes, onChange }: { codes: CdtCode[]; onChange: (c: CdtCode[]) => void }) {
  const [text, setText] = useState('');
  const [error, setError] = useState('');

  const resolve = (raw: string): CdtCode | null => {
    const s = raw.trim().toUpperCase();
    if (!s) return null;
    const byCode = fx.cdtCatalog.find((c) => c.code === s);
    if (byCode) return byCode;
    const byName = fx.cdtCatalog.find((c) => c.name.toLowerCase() === raw.trim().toLowerCase());
    return byName ?? null;
  };

  const addMany = (raw: string) => {
    const parts = raw.split(/[\s,\n]+/).map((p) => p.trim()).filter(Boolean);
    if (!parts.length) return;
    const next = [...codes];
    const unknowns: string[] = [];
    for (const part of parts) {
      const hit = resolve(part);
      if (!hit) { unknowns.push(part); continue; }
      if (!next.some((c) => c.code === hit.code)) next.push(hit);
    }
    onChange(next);
    if (unknowns.length) { setError(`Not a known code: ${unknowns.join(', ')}`); setText(unknowns.join(' ')); }
    else { setError(''); setText(''); }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          className="lc-input"
          style={{ flex: 1 }}
          placeholder="A CDT code, or type a procedure name"
          aria-label="Add a procedure to price"
          value={text}
          onChange={(e) => { setText(e.target.value); setError(''); }}
          onPaste={(e) => { const t = e.clipboardData.getData('text'); if (/[\s,\n]/.test(t)) { e.preventDefault(); addMany(t); } }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); addMany(text); }
            else if (e.key === 'Backspace' && !text && codes.length) { onChange(codes.slice(0, -1)); }
          }}
        />
        <button type="button" className="lc-btn" onClick={() => { addMany(text); }}>Add</button>
      </div>
      {error ? <div className="lc-err">{error}</div> : null}
      {codes.length ? (
        <div className="lc-chips">
          {codes.map((c) => (
            <span key={c.code} className="lc-chip">
              <span className="code">{c.code}</span> {c.name}
              <button type="button" className="lc-x" aria-label={`Remove ${c.code} ${c.name}`} onClick={() => { onChange(codes.filter((x) => x.code !== c.code)); }}>×</button>
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/* ── The form ──────────────────────────────────────────────────────────── */
export function LiveCheckForm() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const pf = fx.prefilled;

  const [payer, setPayer] = useState<PayerOption | null>(fx.payers.find((p) => p.id === pf.payerId) ?? null);
  const [memberId, setMemberId] = useState(pf.memberId);
  const [group, setGroup] = useState(pf.group);
  const [isSubscriber, setIsSubscriber] = useState(true);
  const [firstName, setFirstName] = useState(pf.firstName);
  const [lastName, setLastName] = useState(pf.lastName);
  const [dob, setDob] = useState(pf.dob);
  const [codes, setCodes] = useState<CdtCode[]>(pf.procedures);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [running, setRunning] = useState(false);
  const [seconds, setSeconds] = useState(0);

  const payerRef = useRef<HTMLInputElement | null>(null);
  const memberRef = useRef<HTMLInputElement | null>(null);
  const firstRef = useRef<HTMLInputElement | null>(null);
  const lastRef = useRef<HTMLInputElement | null>(null);
  const dobRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!running) return;
    const iv = setInterval(() => { setSeconds((s) => s + 1); }, 1000);
    const done = setTimeout(() => { void navigate(`/live-check/${fx.report.checkId}`); }, 2400);
    return () => { clearInterval(iv); clearTimeout(done); };
  }, [running, navigate]);

  const submit = () => {
    const errs: Record<string, string> = {};
    if (!payer) errs.payer = 'Pick the payer from the list.';
    if (!memberId.trim()) errs.member = 'Enter the member ID from the card.';
    if (!firstName.trim()) errs.first = 'Enter the first name.';
    if (!lastName.trim()) errs.last = 'Enter the last name.';
    if (!dob.trim()) errs.dob = 'Enter the date of birth.';
    setErrors(errs);
    if (Object.keys(errs).length) {
      const order: [string, React.RefObject<HTMLInputElement | null>][] = [['payer', payerRef], ['member', memberRef], ['first', firstRef], ['last', lastRef], ['dob', dobRef]];
      for (const [k, ref] of order) { if (errs[k]) { ref.current?.focus(); break; } }
      return;
    }
    setSeconds(0);
    setRunning(true);
  };

  if (params.get('tab') === 'all') {
    return (
      <AllChecks
        onOpen={(id) => { void navigate(`/live-check/${id}`); }}
        onBack={() => { const n = new URLSearchParams(params); n.delete('tab'); n.delete('q'); n.delete('win'); n.delete('n'); setParams(n, { replace: true }); }}
      />
    );
  }

  if (running) {
    return (
      <div className="lc">
        <h1 className="lc-h1">Live Check</h1>
        <div className="lc-card lc-result" aria-live="polite">
          <div className="lc-result-head">
            <h2>Checking with {payer?.name}</h2>
            <span className="lc-tag lc-tag-warn">Running</span>
          </div>
          <ul className="lc-running">
            <li>✓&nbsp; Payer matched — {payer?.name}, {payer?.id}</li>
            <li>✓&nbsp; Request sent, 9:42:06 AM</li>
            <li>·&nbsp; {seconds >= 45 ? 'This is taking longer than usual — ELVA is still trying.' : 'Waiting for the payer…'} {seconds} seconds</li>
          </ul>
          <p className="lc-note">Most payers answer within 30 seconds. ELVA keeps going if you navigate away — the result lands in Checked today.</p>
          <div className="lc-actions" style={{ marginTop: 12 }}>
            {seconds >= 45 ? <button className="lc-btn" onClick={() => { setRunning(false); }}>Retry in the background</button> : null}
            <button className="lc-btn" onClick={() => { setRunning(false); }}>Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="lc">
      <h1 className="lc-h1">Check a patient&rsquo;s eligibility now</h1>
      <p className="lc-lede">For someone at the counter or on the phone. ELVA runs the check against the payer and returns the same report it produces for a booked patient.</p>

      <div className="lc-cols">
        <div className="lc-col-form">
          {/* find the patient */}
          <section className="lc-card">
            <h2 className="lc-card-title">Is this patient already in your system?</h2>
            <p className="lc-card-sub">If they are, nothing needs typing. Search and ELVA fills the rest in. If not, enter the plan below from their card.</p>
            <div className="lc-field">
              <label className="lc-label" htmlFor="lc-search">Name, date of birth or chart number</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input id="lc-search" className="lc-input" style={{ flex: 1 }} placeholder="Search patients" />
                <button type="button" className="lc-btn">Search</button>
              </div>
            </div>
          </section>

          {/* who and which plan */}
          <section className="lc-card">
            <h2 className="lc-card-title">Who and which plan</h2>
            <p className="lc-card-sub">Taken from the card the patient hands you</p>

            <div className="lc-field">
              <label className="lc-label" id="lc-payer-label">Payer<span className="req">*</span></label>
              <PayerCombobox value={payer} onChange={(p) => { setPayer(p); setErrors((e) => ({ ...e, payer: '' })); }} inputRef={payerRef} invalid={!!errors.payer} />
              {errors.payer ? <div className="lc-err">{errors.payer}</div> : <div className="lc-hint">Picked from the payer list, never typed. A free-text carrier name is the single commonest cause of a failed check.</div>}
            </div>

            <div className="lc-row2">
              <div className="lc-field">
                <label className="lc-label" htmlFor="lc-member">Member ID<span className="req">*</span></label>
                <input id="lc-member" ref={memberRef} className="lc-input" aria-invalid={!!errors.member} value={memberId}
                  onChange={(e) => { setMemberId(e.target.value); }} onBlur={() => { setMemberId((m) => m.trim().toUpperCase()); }} />
                {errors.member ? <div className="lc-err">{errors.member}</div> : null}
              </div>
              <div className="lc-field">
                <label className="lc-label" htmlFor="lc-group">Group number</label>
                <input id="lc-group" className="lc-input" placeholder="optional" value={group} onChange={(e) => { setGroup(e.target.value); }} />
              </div>
            </div>

            <div className="lc-field">
              <label className="lc-label">Is the patient the subscriber?</label>
              <div className="lc-seg" role="group" aria-label="Is the patient the subscriber?">
                <button type="button" aria-pressed={isSubscriber} onClick={() => { setIsSubscriber(true); }}>Yes, the patient</button>
                <button type="button" aria-pressed={!isSubscriber} onClick={() => { setIsSubscriber(false); }}>No, a dependent</button>
              </div>
            </div>

            {!isSubscriber ? (
              <div className="lc-row2">
                <div className="lc-field"><label className="lc-label" htmlFor="lc-sub-first">Subscriber first name</label><input id="lc-sub-first" className="lc-input" /></div>
                <div className="lc-field"><label className="lc-label" htmlFor="lc-sub-last">Subscriber last name</label><input id="lc-sub-last" className="lc-input" /></div>
                <div className="lc-field"><label className="lc-label" htmlFor="lc-sub-dob">Subscriber date of birth</label><input id="lc-sub-dob" type="date" className="lc-input" /></div>
                <div className="lc-field"><label className="lc-label" htmlFor="lc-sub-rel">Relationship</label><input id="lc-sub-rel" className="lc-input" placeholder="Spouse, child…" /></div>
              </div>
            ) : null}

            <div className="lc-row2">
              <div className="lc-field">
                <label className="lc-label" htmlFor="lc-first">First name<span className="req">*</span></label>
                <input id="lc-first" ref={firstRef} className="lc-input" aria-invalid={!!errors.first} value={firstName} onChange={(e) => { setFirstName(e.target.value); }} />
                {errors.first ? <div className="lc-err">{errors.first}</div> : null}
              </div>
              <div className="lc-field">
                <label className="lc-label" htmlFor="lc-last">Last name<span className="req">*</span></label>
                <input id="lc-last" ref={lastRef} className="lc-input" aria-invalid={!!errors.last} value={lastName} onChange={(e) => { setLastName(e.target.value); }} />
                {errors.last ? <div className="lc-err">{errors.last}</div> : null}
              </div>
            </div>

            <div className="lc-row2">
              <div className="lc-field">
                <label className="lc-label" htmlFor="lc-dob">Date of birth<span className="req">*</span></label>
                <input id="lc-dob" ref={dobRef} type="date" className="lc-input" aria-invalid={!!errors.dob} value={dob} onChange={(e) => { setDob(e.target.value); }} />
                {errors.dob ? <div className="lc-err">{errors.dob}</div> : null}
              </div>
              <div className="lc-field">
                <label className="lc-label" htmlFor="lc-dos">Date of service</label>
                <input id="lc-dos" type="date" className="lc-input" defaultValue={fx.today} />
                <div className="lc-hint">Benefits are checked as of this date.</div>
              </div>
            </div>

            <div className="lc-field">
              <label className="lc-label">Provider</label>
              <div className="lc-input" style={{ display: 'flex', alignItems: 'center' }}>{fx.defaultProvider.name} · NPI {fx.defaultProvider.npi}</div>
              <div className="lc-hint">Decides the in-network answer.</div>
            </div>
          </section>

          {/* procedures */}
          <section className="lc-card">
            <h2 className="lc-card-title">Procedures to price <span style={{ fontWeight: 400, color: 'var(--color-muted-foreground,#6b7b79)' }}>optional</span></h2>
            <p className="lc-card-sub">Add what you are planning and the report comes back with a patient estimate as well as the benefits. Leave it empty and you get coverage, maximums and frequencies without the money.</p>
            <CodeChipInput codes={codes} onChange={setCodes} />
          </section>

          <div className="lc-submit-row">
            <button className="lc-btn-primary" disabled={running} onClick={submit}>Run the check</button>
            <button className="lc-btn" onClick={() => { setPayer(null); setMemberId(''); setGroup(''); setFirstName(''); setLastName(''); setDob(''); setCodes([]); setErrors({}); }}>Clear</button>
            <span className="lc-submit-note">Usually answers in 10–30 seconds.</span>
          </div>
        </div>

        {/* side column */}
        <div className="lc-col-side">
          <section className="lc-card">
            <h2 className="lc-card-title">What you will get</h2>
            <p className="lc-card-sub" style={{ marginBottom: 0 }}>The same report as a booked patient: active coverage, maximums and deductibles with what is left, coverage by category, frequencies and when each service is next due, this payer&rsquo;s own rules, and a patient estimate when you add procedures. Printable, and citable if a claim is later denied.</p>
          </section>

          <section className="lc-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <h3 className="lc-side-title">Checked today</h3>
              <span className="lc-today-count">{fx.checkedToday.length + 2}</span>
            </div>
            {fx.checkedToday.map((r) => (
              <div className="lc-today-row" key={r.id}>
                <a href={`/live-check/${r.id}`} onClick={(e) => { if (!isPlainClick(e)) return; e.preventDefault(); void navigate(`/live-check/${r.id}`); }}>{r.name}</a>
                <span className="lc-today-meta">{r.payer} · {r.at} · {r.outcome}</span>
              </div>
            ))}
            <div style={{ marginTop: 10 }}><Link className="lc-btn" style={{ minHeight: 34 }} replace to={{ search: `?${new URLSearchParams([...params.entries(), ['tab', 'all']]).toString()}` }}>All live checks</Link></div>
          </section>

          <section className="lc-card">
            <h3 className="lc-side-title">A check is not a record</h3>
            <p className="lc-card-sub" style={{ marginBottom: 0 }}>A live check for someone who is not your patient yet is kept for 90 days and is not added to the chart. When you book them, attach the check and it becomes part of their record.</p>
          </section>
        </div>
      </div>
      <p className="lc-note" style={{ marginTop: 18 }}>Benefits are checked as of {formatDate(fx.today)}.</p>
    </div>
  );
}
